import { FC, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { AI_ASSIGNABLE_RECRUITERS, AI_BULK_ASSIGN } from './gql';

// ---------------------------------------------------------------------------
// Props contract — do NOT change
// ---------------------------------------------------------------------------
export interface JobOrderTarget {
  jobOrderId: string;
  jobTitle: string | null | undefined;
}

export interface AssignRecruiterModalProps {
  isOpen: boolean;
  jobOrderTarget: JobOrderTarget | null;
  selectedIds: string[];
  onClose: () => void;
  onAssigned: () => void;
}

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

interface AssignableUser {
  id: string;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  fullName: string | null | undefined;
  status: string | null | undefined;
}

// `status` is a code-first GraphQL enum, so it serializes as the enum KEY ('ACTIVE'),
// not the underlying value ('Active').
const isActiveRecruiter = (user: AssignableUser): boolean =>
  user.status === 'ACTIVE';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AssignRecruiterModal: FC<AssignRecruiterModalProps> = ({
  isOpen,
  jobOrderTarget,
  selectedIds,
  onClose,
  onAssigned,
}) => {
  const [selectedRecruiterId, setSelectedRecruiterId] = useState<string | null>(null);

  const { data, loading: queryLoading, error: queryError } = useQuery<{
    aiRecruiterAssignableRecruiters: AssignableUser[];
  }>(AI_ASSIGNABLE_RECRUITERS, {
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  const [bulkAssign, { loading: assigning }] = useMutation(AI_BULK_ASSIGN);

  const recruiters: AssignableUser[] = data?.aiRecruiterAssignableRecruiters ?? [];

  const handleClose = () => {
    setSelectedRecruiterId(null);
    onClose();
  };

  const handleAssign = async () => {
    if (!selectedRecruiterId) return;
    try {
      await bulkAssign({
        variables: {
          input: {
            pipelineEntryIds: selectedIds,
            recruiterUserId: selectedRecruiterId,
          },
        },
      });
      setSelectedRecruiterId(null);
      onAssigned();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Assign failed: ${message}`);
    }
  };

  const n = selectedIds.length;
  const jobTitle = jobOrderTarget?.jobTitle;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} style={{ maxWidth: 500 }}>
      <ModalHeader toggle={handleClose}>Assign to Recruiter</ModalHeader>

      <ModalBody>
        <p style={{ marginBottom: 16 }}>
          Assign{' '}
          <strong>
            {n} applicant{n !== 1 ? 's' : ''}
          </strong>{' '}
          in{' '}
          <strong>{jobTitle ?? 'this job order'}</strong>{' '}
          to one active recruiter. The needs-human reason is preserved.
        </p>

        {queryLoading && recruiters.length === 0 ? (
          <div className="text-center py-3">
            <Spinner size="sm" color="warning" />
          </div>
        ) : queryError ? (
          <p className="text-danger">Failed to load recruiters: {queryError.message}</p>
        ) : recruiters.length === 0 ? (
          <p className="text-muted">No recruiters available.</p>
        ) : (
          <div
            style={{
              border: '1px solid #eeeeee',
              borderRadius: 4,
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            {recruiters.map((recruiter) => {
              const active = isActiveRecruiter(recruiter);
              const isSelected = selectedRecruiterId === recruiter.id;

              return (
                <div
                  key={recruiter.id}
                  onClick={() => {
                    if (active) setSelectedRecruiterId(recruiter.id);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    cursor: active ? 'pointer' : 'not-allowed',
                    opacity: active ? 1 : 0.45,
                    borderBottom: '1px solid #eeeeee',
                    background: isSelected ? '#ecf5ff' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                >
                  <input
                    type="radio"
                    readOnly
                    checked={isSelected}
                    disabled={!active}
                    style={{ flexShrink: 0, cursor: active ? 'pointer' : 'not-allowed' }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => {
                      if (active) setSelectedRecruiterId(recruiter.id);
                    }}
                  />
                  <span style={{ flex: 1, fontSize: '0.9rem' }}>
                    {recruiter.fullName ??
                      ([recruiter.firstName, recruiter.lastName].filter(Boolean).join(' ') ||
                        '(Unknown)')}
                  </span>
                  {!active && (
                    <span
                      style={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        padding: '1px 5px',
                        borderRadius: 4,
                        background: '#6c757d',
                        color: '#fff',
                        flexShrink: 0,
                      }}
                    >
                      INACTIVE
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={assigning}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleAssign}
          disabled={!selectedRecruiterId || assigning}
        >
          {assigning ? <Spinner size="sm" /> : 'Assign'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignRecruiterModal;
